export const PICTIONARY_TRANSLATION_SUPPORT = {
    revealMs: 8000, startRechargeMs: 4000, minRechargeMs: 1000, maxRechargeMs: 12000, stepMs: 1000
} as const;

export function clampPictionaryTranslationRechargeMs(value: number | null | undefined): number {
    return typeof value === "number" && Number.isFinite(value)
        ? Math.max(PICTIONARY_TRANSLATION_SUPPORT.minRechargeMs,
            Math.min(PICTIONARY_TRANSLATION_SUPPORT.maxRechargeMs, Math.round(value)))
        : PICTIONARY_TRANSLATION_SUPPORT.startRechargeMs;
}

export function getLegacyPictionaryTranslationRechargeMs(rankFraction: number): number {
    const rank = Number.isFinite(rankFraction) ? Math.max(0, Math.min(1, rankFraction)) : 0;
    return Math.round(1000 + 7000 * rank);
}

export type PictionaryTranslationOutcome = {
    sessionId: string; guesses: number; solveRank: number | null; connected: boolean;
};
export type PictionaryTranslationAdjustment = {
    sessionId: string; signal: -1 | 0 | 1;
    reason: "clean-early" | "struggle" | "ambiguous" | "unclear-case";
};

/** Use only the frozen case-start roster; late joins cannot consume early-solve slots. */
export function getPictionaryTranslationAdjustments(outcomes: readonly PictionaryTranslationOutcome[],
    teamCount: number, caseExpired: boolean): PictionaryTranslationAdjustment[] {
    const half = Math.ceil(Math.max(0, teamCount) / 2);
    const solved = outcomes.filter(p => p.solveRank !== null && p.solveRank > 0)
        .sort((a, b) => a.solveRank! - b.solveRank!);
    const clearCase = half > 0 && solved.filter(p => p.guesses <= 2).length >= half;
    return outcomes.map(p => {
        if (!clearCase) return { sessionId: p.sessionId, signal: 0, reason: "unclear-case" };
        const solvedIndex = solved.findIndex(s => s.sessionId === p.sessionId);
        if (p.guesses === 1 && solvedIndex >= 0 && solvedIndex < half)
            return { sessionId: p.sessionId, signal: 1, reason: "clean-early" };
        // An interrupted/disconnected unsolved attempt is not evidence of a full-case struggle.
        if ((solvedIndex >= 0 && p.guesses >= 3) ||
            (solvedIndex < 0 && p.guesses > 0 && p.connected && caseExpired))
            return { sessionId: p.sessionId, signal: -1, reason: "struggle" };
        return { sessionId: p.sessionId, signal: 0, reason: "ambiguous" };
    });
}

/** Legacy rank behavior for an in-flight case opened before personal support existed. */
export function getPictionaryTranslationRankFraction(score: number, scores: readonly number[]): number {
    const standings = scores.filter(Number.isFinite);
    if (!Number.isFinite(score) || standings.length < 2) return 0;
    const lowest = Math.min(...standings);
    const lowestRank = standings.filter((value) => value > lowest).length;
    if (lowestRank === 0) return 0;
    const rank = standings.filter((value) => value > score).length;
    return Math.max(0, Math.min(1, (lowestRank - rank) / lowestRank));
}
